/**
 * Environment Configuration Module
 *
 * Centralizes all environment-specific configuration for the StudyStride application.
 * Values are loaded from Vite environment variables (VITE_* prefix).
 *
 * This module must be imported first (e.g. in main.tsx) so config is initialized
 * before any other app code runs - avoids TDZ in production bundle.
 *
 * Usage:
 *   import { config } from '@/config/environment';
 *   // Core (student + admin + curriculum): config.coreApiUrl
 *   // Scottish curriculum (separate service): config.scottishCurriculumApiUrl
 */
export const brand = {
  name: 'StudyStride',
  tagline: 'Step Forward in Learning',
  domain: 'studystride.app',
  url: 'https://studystride.app',
  description: 'Track your learning progress, achieve your goals, and excel in education.',
  copyright: `© ${new Date().getFullYear()} StudyStride. All rights reserved.`,
  social: {
    twitter: '@studystride',
    email: 'hello@studystride.app',
  },
  colors: {
    primary: '#2563EB',
    secondary: '#0D9488',
    success: '#16A34A',
    dark: '#1E293B',
    light: '#F8FAFC',
  },
} as const;

export type Environment = 'development' | 'test' | 'production';

export interface EnvironmentConfig {
  /** Core service URL (student + admin + curriculum). Use for auth, users, courses, curriculum, guest, etc. */
  coreApiUrl: string;
  /** Scottish curriculum service URL (separate service). Use only for Scottish hierarchy/areas/experience outcomes. */
  scottishCurriculumApiUrl: string;
  environment: Environment;
  isProduction: boolean;
  isDevelopment: boolean;
  features: {
    analytics: boolean;
    debug: boolean;
    experimental: boolean;
  };
  services: {
    sentryDsn?: string;
    gaId?: string;
    googleClientId?: string;
    recaptchaSiteKey?: string;
  };
}

const getEnvironment = (): Environment => {
  const env = import.meta.env.VITE_ENVIRONMENT;
  if (env === 'production') return 'production';
  if (env === 'test') return 'test';
  if (env === 'development') return 'development';
  // Vercel/CI often set only VITE_* API URLs; vite build sets import.meta.env.PROD
  if (import.meta.env.PROD) return 'production';
  return 'development';
};

const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined) return defaultValue;
  return value === 'true';
};

// Ensures a URL read from an env var always has a protocol. A misconfigured env var
// like "api.example.com" (no https://) would be treated as a relative path by the browser,
// sending requests to the Vercel origin instead of the backend.
const withProtocol = (url: string, fallback: string): string => {
  if (!url) return fallback;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

// Single core URL (student + admin + curriculum). Production: set VITE_CORE_API_URL in Vercel.
// Preview (optional): set VITE_* in Vercel or use scripts/vercel-build.sh as build command when enabling PR-preview API URLs (see docs/deployment/preview-pull-requests-coolify-vercel-neon.md).
const coreUrl = withProtocol(
  import.meta.env.VITE_CORE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_STUDENT_API_URL ||
  import.meta.env.VITE_CURRICULUM_API_URL || '',
  'http://localhost:8081'
);

export const config: EnvironmentConfig = {
  coreApiUrl: coreUrl,
  scottishCurriculumApiUrl: withProtocol(
    import.meta.env.VITE_SCOTTISH_CURRICULUM_API_URL || '',
    'http://localhost:8082'
  ),
  environment: getEnvironment(),
  isProduction: getEnvironment() === 'production',
  isDevelopment: getEnvironment() === 'development',
  features: {
    analytics: parseBoolean(import.meta.env.VITE_ENABLE_ANALYTICS, false),
    // Default off for production bundles so console noise and "dev" checks match real deploys
    debug: parseBoolean(import.meta.env.VITE_ENABLE_DEBUG, import.meta.env.DEV),
    experimental: parseBoolean(import.meta.env.VITE_ENABLE_EXPERIMENTAL, false),
  },
  services: {
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    gaId: import.meta.env.VITE_GA_ID,
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  },
};

if (config.isDevelopment && config.features.debug) {
  console.log('📚', brand.name, '-', brand.tagline);
  console.log('🔧 Environment:', { environment: config.environment, coreApiUrl: config.coreApiUrl });
}
if (config.isProduction && !config.coreApiUrl.startsWith('https://')) {
  console.warn('⚠️ Production API URL should use HTTPS');
}

export default config;
